import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { CheckCircle, DollarSign, DownloadCloud, Eye, GitCommit, History, LockKeyhole, Receipt, RefreshCw, User } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

type GitHubSyncError = {
  at: string;
  action: 'check-new' | 'reload-all' | 'repo-snapshot' | 'other';
  message: string;
};

export type DeveloperControlsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  canManageDeveloperSettings: boolean;
  developerPasscode: string;
  setDeveloperPasscode: Dispatch<SetStateAction<string>>;
  handleUnlockDeveloper: () => void;

  rateDraft: string;
  setRateDraft: Dispatch<SetStateAction<string>>;
  rateDraftDirtyRef: MutableRefObject<boolean>;
  handleSaveRate: () => void;

  hourlyRateDraft: string;
  setHourlyRateDraft: Dispatch<SetStateAction<string>>;
  hourlyRateDraftDirtyRef: MutableRefObject<boolean>;
  handleSaveHourlyRate: () => void;

  lastGitHubSyncError: GitHubSyncError | null;

  repoOwner: string;
  setRepoOwner: Dispatch<SetStateAction<string>>;
  repoOwnerDirtyRef: MutableRefObject<boolean>;
  repoName: string;
  setRepoName: Dispatch<SetStateAction<string>>;
  repoNameDirtyRef: MutableRefObject<boolean>;
  handleSaveRepoSettings: () => void;

  loadingNewCommits: boolean;
  fetchNewCommitsFromGitHub: () => void;

  gitHubProxyPingLoading: boolean;
  handlePingGitHubProxy: () => void;

  developerUnlocked: boolean;
  isAdminUser: boolean;
  handleCheckGitHubAuth: () => void;

  repoFilesLoading: boolean;
  repoSnapshotCommitSha: string | null;
  fetchRepoSnapshot: () => void;

  loadingAllCommits: boolean;
  setReloadDialogOpen: Dispatch<SetStateAction<boolean>>;

  expenseDate: string;
  setExpenseDate: Dispatch<SetStateAction<string>>;
  expenseCategory: string;
  setExpenseCategory: Dispatch<SetStateAction<string>>;
  expenseAmount: string;
  setExpenseAmount: Dispatch<SetStateAction<string>>;
  expenseAttachment: File | null;
  setExpenseAttachment: Dispatch<SetStateAction<File | null>>;
  expenseNotes: string;
  setExpenseNotes: Dispatch<SetStateAction<string>>;
  expenseJournalEntry: string;
  setExpenseJournalEntry: Dispatch<SetStateAction<string>>;
  handleAddExpense: () => void;
};

export function DeveloperControlsDialog({
  open,
  onOpenChange,
  canManageDeveloperSettings,
  developerPasscode,
  setDeveloperPasscode,
  handleUnlockDeveloper,
  rateDraft,
  setRateDraft,
  rateDraftDirtyRef,
  handleSaveRate,
  hourlyRateDraft,
  setHourlyRateDraft,
  hourlyRateDraftDirtyRef,
  handleSaveHourlyRate,
  lastGitHubSyncError,
  repoOwner,
  setRepoOwner,
  repoOwnerDirtyRef,
  repoName,
  setRepoName,
  repoNameDirtyRef,
  handleSaveRepoSettings,
  loadingNewCommits,
  fetchNewCommitsFromGitHub,
  gitHubProxyPingLoading,
  handlePingGitHubProxy,
  developerUnlocked,
  isAdminUser,
  handleCheckGitHubAuth,
  repoFilesLoading,
  repoSnapshotCommitSha,
  fetchRepoSnapshot,
  loadingAllCommits,
  setReloadDialogOpen,
  expenseDate,
  setExpenseDate,
  expenseCategory,
  setExpenseCategory,
  expenseAmount,
  setExpenseAmount,
  setExpenseAttachment,
  expenseNotes,
  setExpenseNotes,
  expenseJournalEntry,
  setExpenseJournalEntry,
  handleAddExpense,
}: DeveloperControlsDialogProps) {
  const hasExistingSnapshot = (repoSnapshotCommitSha ?? '').trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LockKeyhole className="w-5 h-5 text-primary" />
            Developer Controls
          </DialogTitle>
          <DialogDescription>Enter the passcode to manage billing settings and uploads.</DialogDescription>
        </DialogHeader>
        <div className="space-y-6">
          {!canManageDeveloperSettings ? (
            <div className="rounded-lg border border-border/60 bg-muted/20 p-4 space-y-4">
              <Input
                type="password"
                placeholder="Enter passcode"
                autoFocus
                value={developerPasscode}
                onChange={(event) => setDeveloperPasscode(event.target.value)}
              />
              <DialogFooter>
                <Button onClick={handleUnlockDeveloper} className="gap-2">
                  <LockKeyhole className="w-4 h-4" />
                  Unlock dialog
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <>
              <div className="rounded-lg border border-border/60 bg-muted/20 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Rate per commit</p>
                    <p className="text-xs text-muted-foreground">
                      Stored in Firestore for billing calculations (or locally when offline).
                    </p>
                  </div>
                  <DollarSign className="w-5 h-5 text-primary" />
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={rateDraft}
                    onChange={(event) => {
                      setRateDraft(event.target.value);
                      rateDraftDirtyRef.current = true;
                    }}
                    className="sm:max-w-[160px]"
                  />
                  <Button onClick={handleSaveRate} className="gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Save rate
                  </Button>
                </div>
              </div>

              <div className="rounded-lg border border-border/60 bg-muted/20 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Rate per hour</p>
                    <p className="text-xs text-muted-foreground">Default hourly rate used for time + workorder entries.</p>
                  </div>
                  <DollarSign className="w-5 h-5 text-primary" />
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={hourlyRateDraft}
                    onChange={(event) => {
                      setHourlyRateDraft(event.target.value);
                      hourlyRateDraftDirtyRef.current = true;
                    }}
                    className="sm:max-w-[160px]"
                  />
                  <Button onClick={handleSaveHourlyRate} className="gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Save rate
                  </Button>
                </div>
              </div>

              <div className="rounded-lg border border-border/60 bg-muted/20 p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">GitHub sync</p>
                    <p className="text-xs text-muted-foreground">
                      Store the repository details once, then manually add new commits as they arrive.
                    </p>
                  </div>
                  <GitCommit className="w-5 h-5 text-primary" />
                </div>
                {lastGitHubSyncError &&
                  (lastGitHubSyncError.action === 'check-new' || lastGitHubSyncError.action === 'reload-all') && (
                    <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
                      <p className="font-semibold">Last GitHub sync error</p>
                      <p className="mt-1 break-words">{lastGitHubSyncError.message}</p>
                    </div>
                  )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input
                    placeholder="GitHub owner (e.g. org)"
                    value={repoOwner}
                    onChange={(event) => {
                      setRepoOwner(event.target.value);
                      repoOwnerDirtyRef.current = true;
                    }}
                  />
                  <Input
                    placeholder="Repository name"
                    value={repoName}
                    onChange={(event) => {
                      setRepoName(event.target.value);
                      repoNameDirtyRef.current = true;
                    }}
                  />
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <Button onClick={handleSaveRepoSettings} variant="outline" className="gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Save GitHub settings
                  </Button>
                  <Button onClick={fetchNewCommitsFromGitHub} disabled={loadingNewCommits} className="gap-2">
                    <History className={`w-4 h-4 ${loadingNewCommits ? 'animate-spin' : ''}`} />
                    {loadingNewCommits ? 'Checking...' : 'Check for new commits'}
                  </Button>
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <Button
                    onClick={handlePingGitHubProxy}
                    disabled={gitHubProxyPingLoading}
                    variant="outline"
                    className="gap-2"
                  >
                    <RefreshCw className={`w-4 h-4 ${gitHubProxyPingLoading ? 'animate-spin' : ''}`} />
                    {gitHubProxyPingLoading ? 'Testing proxy...' : 'Test GitHub proxy'}
                  </Button>
                  {(developerUnlocked || isAdminUser) && (
                    <Button onClick={handleCheckGitHubAuth} variant="outline" className="gap-2">
                      <User className="w-4 h-4" />
                      Check GitHub auth
                    </Button>
                  )}
                  <span className="text-xs text-muted-foreground">Uses the deployed /api/github backend proxy.</span>
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  {developerUnlocked || isAdminUser ? (
                    <Button
                      onClick={() => fetchRepoSnapshot()}
                      disabled={repoFilesLoading || hasExistingSnapshot}
                      variant="secondary"
                      className="gap-2"
                    >
                      <Eye className={`w-4 h-4 ${repoFilesLoading ? 'animate-spin' : ''}`} />
                      {hasExistingSnapshot ? 'Snapshot generated' : repoFilesLoading ? 'Generating…' : 'Generate code snapshot'}
                    </Button>
                  ) : (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <LockKeyhole className="w-4 h-4" />
                      Developer passcode required to import preview data.
                    </div>
                  )}
                  <span className="text-xs text-muted-foreground">
                    Snapshot redacts sensitive keys before admins can view or download.
                  </span>
                </div>
                <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-muted-foreground">
                  Reloading all commits clears Firestore history and re-downloads every commit.
                </div>
                <div className="flex justify-end">
                  <Button
                    onClick={() => setReloadDialogOpen(true)}
                    disabled={loadingAllCommits}
                    variant="destructive"
                    className="gap-2"
                  >
                    <DownloadCloud className={`w-4 h-4 ${loadingAllCommits ? 'animate-bounce' : ''}`} />
                    {loadingAllCommits ? 'Reloading...' : 'Reload all commits'}
                  </Button>
                </div>
              </div>

              <div className="rounded-lg border border-border/60 bg-muted/20 p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Expense upload + journal entry</p>
                    <p className="text-xs text-muted-foreground">Attach receipts (image/PDF) with an internal journal note.</p>
                  </div>
                  <Receipt className="w-5 h-5 text-primary" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input type="date" value={expenseDate} onChange={(event) => setExpenseDate(event.target.value)} />
                  <Input
                    placeholder="Category"
                    value={expenseCategory}
                    onChange={(event) => setExpenseCategory(event.target.value)}
                  />
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Amount"
                    value={expenseAmount}
                    onChange={(event) => setExpenseAmount(event.target.value)}
                  />
                  <Input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={(event) => setExpenseAttachment(event.target.files?.[0] ?? null)}
                  />
                </div>
                <Input
                  placeholder="Expense notes (optional)"
                  value={expenseNotes}
                  onChange={(event) => setExpenseNotes(event.target.value)}
                />
                <Input
                  placeholder="Journal entry for this expense"
                  value={expenseJournalEntry}
                  onChange={(event) => setExpenseJournalEntry(event.target.value)}
                />
                <div className="flex justify-end">
                  <Button onClick={handleAddExpense} className="gap-2">
                    <Receipt className="w-4 h-4" />
                    Log expense
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
